import * as React from 'react';
import NextHead from 'next/head';
import { useRouter } from 'next/router';

const DEFAULT_DESCRIPTION =
  'Real-time options flow, dark pool data, options profit calculator, gamma exposure, insider trades, congressional trades, and market news for active traders.';
const DEFAULT_IMAGE = `${process.env.ROOT_URL}/social-share.jpg`;

interface Props {
  title?: string;
  description?: string;
  url?: string;
  ogImage?: string;
  ogType?: string;
  preventBrandTitle?: boolean;
  useCurrentUrl?: boolean;
  includeQuery?: boolean;
  noIndex?: boolean;
}

const addBrandTitle = (title?: string) => `${title} | InsiderFinance`;

const getTitle = (title?: string, preventBrandTitle?: boolean) => {
  if (!title) {
    return '';
  }

  return preventBrandTitle ? title : addBrandTitle(title);
};

const getPathWithoutQuery = (path: string) => {
  if (!path) return '';

  try {
    // Use URL API to properly parse and extract pathname
    const url = new URL(path, 'https://www.insiderfinance.io');
    return url.pathname;
  } catch {
    // Fallback to string splitting if URL parsing fails
    return path.split('?')[0].split('#')[0];
  }
};

const Head: React.FC<React.PropsWithChildren<Props>> = ({
  title,
  description,
  url,
  ogImage,
  ogType,
  useCurrentUrl,
  includeQuery = false,
  preventBrandTitle,
  noIndex,
}) => {
  const router = useRouter();
  const finalTitle = getTitle(title, preventBrandTitle);

  // Get full path (with query) and clean path (without query)
  // Explicit url prop takes precedence; only use current URL if no url provided
  const shouldUseCurrentUrl = useCurrentUrl && !url;
  const fullPath = shouldUseCurrentUrl ? router.asPath : url || '';
  const cleanPath = shouldUseCurrentUrl
    ? getPathWithoutQuery(router.asPath)
    : getPathWithoutQuery(url || '');

  // Use clean path by default for both canonical and og:url (strips query strings for SEO)
  // This consolidates SEO signals and social sharing to a single authoritative URL
  const finalPath = includeQuery ? fullPath : cleanPath;
  const canonicalUrl = finalPath ? `${process.env.ROOT_URL}${finalPath}` : '';

  return (
    <NextHead>
      {(noIndex || process.env.APP_STAGE !== 'production' || process.env.NOINDEX === 'true') && (
        <meta name="robots" content="noindex" />
      )}

      <title>{finalTitle}</title>
      <meta name="description" content={description || DEFAULT_DESCRIPTION} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={description || DEFAULT_DESCRIPTION} />
      <meta property="og:image" content={ogImage || DEFAULT_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="InsiderFinance" />
      {!!ogType && <meta property="og:type" content={ogType} />}
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={description || DEFAULT_DESCRIPTION} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={ogImage || DEFAULT_IMAGE} />
      {!!canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
    </NextHead>
  );
};

export default Head;
